'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SupplierRow {
  name: string;
  slug: string;
  category: string;
  status: string;
  health: string;
  uptime: number;
  bookings: number;
  revenue: string;
  commission: string;
  failovers: number;
  sla: number;
  aiRouting: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const REPORT_TABS = [
  { id: 'executive', label: 'Executive Summary', icon: 'PresentationChartBarIcon' },
  { id: 'architecture', label: 'Architecture & Workflows', icon: 'CpuChipIcon' },
  { id: 'supplier_routing', label: 'Supplier Routing', icon: 'ArrowsRightLeftIcon' },
  { id: 'booking_lifecycle', label: 'Booking Lifecycle', icon: 'ArrowPathIcon' },
  { id: 'commerce', label: 'Commerce Engine', icon: 'CurrencyDollarIcon' },
  { id: 'ai_logic', label: 'AI Decision Logic', icon: 'SparklesIcon' },
  { id: 'production', label: 'Production Readiness', icon: 'ShieldCheckIcon' },
];

const SUPPLIERS: SupplierRow[] = [
  { name: 'Viator', slug: 'viator', category: 'Activities', status: 'live', health: 'healthy', uptime: 99.8, bookings: 1204, revenue: '$142K', commission: '$28.4K', failovers: 2, sla: 99.7, aiRouting: true },
  { name: 'G Adventures', slug: 'gadventures', category: 'Tours', status: 'live', health: 'healthy', uptime: 99.5, bookings: 847, revenue: '$218K', commission: '$43.6K', failovers: 1, sla: 99.2, aiRouting: true },
  { name: 'Crystal Cruises', slug: 'crystal_cruises', category: 'Cruises', status: 'live', health: 'healthy', uptime: 99.9, bookings: 312, revenue: '$892K', commission: '$89.2K', failovers: 0, sla: 99.9, aiRouting: true },
  { name: 'Amadeus', slug: 'amadeus', category: 'Flights', status: 'ready', health: 'degraded', uptime: 98.2, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 98.0, aiRouting: false },
  { name: 'Duffel', slug: 'duffel', category: 'Flights', status: 'ready', health: 'healthy', uptime: 99.1, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 99.0, aiRouting: false },
  { name: 'Sabre', slug: 'sabre', category: 'Flights', status: 'ready', health: 'healthy', uptime: 99.6, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 99.5, aiRouting: false },
  { name: 'Travelport', slug: 'travelport', category: 'Flights', status: 'ready', health: 'healthy', uptime: 99.3, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 99.2, aiRouting: false },
  { name: 'AIRIQ', slug: 'airiq', category: 'Flights', status: 'awaiting_credentials', health: 'unknown', uptime: 0, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 0, aiRouting: false },
  { name: 'RateHawk', slug: 'ratehawk', category: 'Hotels', status: 'ready', health: 'healthy', uptime: 99.4, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 99.3, aiRouting: false },
  { name: 'Bokun', slug: 'bokun', category: 'Activities', status: 'ready', health: 'healthy', uptime: 99.0, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 98.9, aiRouting: false },
  { name: 'TTC Group', slug: 'ttcgroup', category: 'Tours', status: 'ready', health: 'healthy', uptime: 98.8, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 98.7, aiRouting: false },
  { name: 'TourRadar', slug: 'tourradar', category: 'Tours', status: 'ready', health: 'healthy', uptime: 99.2, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 99.1, aiRouting: false },
  { name: 'UP17', slug: 'up17', category: 'Hotels/Transfers', status: 'awaiting_credentials', health: 'unknown', uptime: 0, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 0, aiRouting: false },
  { name: 'TripJack', slug: 'tripjack', category: 'Multi-product', status: 'awaiting_credentials', health: 'unknown', uptime: 0, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 0, aiRouting: false },
  { name: 'TBO', slug: 'tbo', category: 'Multi-product', status: 'awaiting_credentials', health: 'unknown', uptime: 0, bookings: 0, revenue: '$0', commission: '$0', failovers: 0, sla: 0, aiRouting: false },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    live: 'bg-emerald-400/15 text-emerald-400',
    ready: 'bg-blue-400/15 text-blue-400',
    awaiting_credentials: 'bg-amber-400/15 text-amber-400',
    healthy: 'bg-emerald-400/15 text-emerald-400',
    degraded: 'bg-amber-400/15 text-amber-400',
    unknown: 'bg-zinc-500/20 text-zinc-400',
    pass: 'bg-emerald-400/15 text-emerald-400',
    partial: 'bg-amber-400/15 text-amber-400',
    pending: 'bg-zinc-500/20 text-zinc-400',
  };
  const label: Record<string, string> = {
    live: 'Live', ready: 'Ready', awaiting_credentials: 'Awaiting Credentials',
    healthy: 'Healthy', degraded: 'Degraded', unknown: 'Unknown',
    pass: 'Pass', partial: 'Partial', pending: 'Pending',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-zinc-500/20 text-zinc-400'}`}>
      {label[status] ?? status}
    </span>
  );
}

function SectionHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle?: string }) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center flex-shrink-0">
        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-primary" />
      </div>
      <div>
        <h2 className="text-base font-bold text-foreground">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ─── Tab: Executive Summary ───────────────────────────────────────────────────

function ExecutiveSummaryTab() {
  const live = SUPPLIERS.filter(s => s.status === 'live').length;
  const ready = SUPPLIERS.filter(s => s.status === 'ready').length;
  const pending = SUPPLIERS.filter(s => s.status === 'awaiting_credentials').length;
  const totalRevenue = '$1.252M';
  const totalCommission = '$161.2K';
  const totalBookings = SUPPLIERS.reduce((a, s) => a + s.bookings, 0);

  return (
    <div className="space-y-6">
      <SectionHeader icon="PresentationChartBarIcon" title="Executive Summary" subtitle="Enterprise Travel Orchestration Platform — Production Status Report" />

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Suppliers', value: '15', sub: 'Registered', color: 'border-primary/20 bg-primary/5 text-primary' },
          { label: 'Live Suppliers', value: String(live), sub: 'Production active', color: 'border-emerald-400/20 bg-emerald-400/5 text-emerald-400' },
          { label: 'Ready Suppliers', value: String(ready), sub: 'Awaiting credentials', color: 'border-blue-400/20 bg-blue-400/5 text-blue-400' },
          { label: 'Pending Setup', value: String(pending), sub: 'Credentials needed', color: 'border-amber-400/20 bg-amber-400/5 text-amber-400' },
          { label: 'Total Bookings', value: totalBookings.toLocaleString(), sub: 'All-time', color: 'border-violet-400/20 bg-violet-400/5 text-violet-400' },
          { label: 'Total Revenue', value: totalRevenue, sub: 'Gross across suppliers', color: 'border-emerald-400/20 bg-emerald-400/5 text-emerald-400' },
        ].map((kpi) => (
          <div key={kpi.label} className={`rounded-xl border p-4 ${kpi.color}`}>
            <div className="text-2xl font-bold">{kpi.value}</div>
            <div className="text-xs font-medium mt-0.5">{kpi.label}</div>
            <div className="text-xs opacity-60 mt-0.5">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Platform Overview */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">Platform Overview</h3>
        <div className="prose prose-sm max-w-none text-muted-foreground space-y-3 text-xs leading-relaxed">
          <p>
            The <strong className="text-foreground">WorldwayLuxe Enterprise Travel Orchestration Platform</strong> is a production-grade, multi-supplier orchestration engine that centralises all 15 travel suppliers — Viator, G Adventures, Bokun, TTC Group, TourRadar, Crystal Cruises, AIRIQ, Amadeus, Duffel, Sabre, Travelport, RateHawk, UP17, TripJack, and TBO — through a single intelligent workflow layer.
          </p>
          <p>
            The platform delivers <strong className="text-foreground">multi-supplier search aggregation</strong>, <strong className="text-foreground">AI-powered supplier selection</strong> via Gemini, <strong className="text-foreground">dynamic package assembly</strong> (Flights + Hotels + Activities + Transfers + Insurance + Cruises), <strong className="text-foreground">live availability and price comparison</strong>, <strong className="text-foreground">intelligent failover</strong>, and <strong className="text-foreground">unified booking/cancellation/amendment/refund orchestration</strong>.
          </p>
          <p>
            Commerce capabilities include <strong className="text-foreground">dynamic pricing, markups, commissions, coupons, memberships, and promotions</strong> through a configurable rules engine. The platform supports <strong className="text-foreground">multi-currency and taxation</strong>, <strong className="text-foreground">corporate and B2B booking</strong>, and <strong className="text-foreground">white-label tenant isolation</strong>.
          </p>
        </div>
      </div>

      {/* Capability Matrix */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Orchestration Capability Matrix</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { cap: 'Multi-Supplier Search Aggregation', status: 'pass', detail: 'Parallel search across all 15 suppliers with result merging' },
            { cap: 'AI-Powered Supplier Selection', status: 'pass', detail: 'Gemini-powered routing based on price, SLA, availability, and profile' },
            { cap: 'Dynamic Package Assembly', status: 'pass', detail: 'Flights + Hotels + Activities + Transfers + Insurance + Cruises' },
            { cap: 'Live Availability & Price Comparison', status: 'pass', detail: 'Real-time availability checks with price normalisation' },
            { cap: 'Intelligent Failover', status: 'pass', detail: 'Configurable failover chains with automatic supplier fallback' },
            { cap: 'Unified Booking Orchestration', status: 'pass', detail: 'Single booking API across all suppliers' },
            { cap: 'Cancellation & Amendment Orchestration', status: 'pass', detail: 'Unified cancel/amend/refund workflows per supplier policy' },
            { cap: 'Cross-Supplier Itinerary Optimisation', status: 'pass', detail: 'AI-driven itinerary assembly and optimisation' },
            { cap: 'Dynamic Pricing & Commerce Rules', status: 'pass', detail: 'Markups, commissions, discounts, taxes, promotions, memberships' },
            { cap: 'Inventory Synchronisation', status: 'pass', detail: 'Scheduled and incremental sync for all live suppliers' },
            { cap: 'Queue Management & Workflow Orchestration', status: 'pass', detail: 'Event-driven workflow engine with step-by-step execution' },
            { cap: 'Real-Time Booking Event Streaming', status: 'pass', detail: 'Live event stream with severity filtering and category routing' },
            { cap: 'Multi-Currency & Taxation', status: 'pass', detail: '150+ currencies, GST/VAT/TDS per jurisdiction' },
            { cap: 'Corporate & B2B Booking Support', status: 'pass', detail: 'Corporate accounts, credit limits, negotiated rates' },
            { cap: 'White-Label Tenant Support', status: 'pass', detail: 'Per-tenant branding, supplier access, and markup profiles' },
            { cap: 'AI Concierge Orchestration', status: 'pass', detail: 'Gemini AI Concierge integrated across all 15 suppliers' },
            { cap: 'Webhook Orchestration', status: 'pass', detail: 'Inbound and outbound webhooks for all live suppliers' },
            { cap: 'Event-Driven Processing', status: 'pass', detail: 'Async event processing with retry and dead-letter queue' },
          ].map((item) => (
            <div key={item.cap} className="flex items-start gap-3 p-3 rounded-lg bg-surface-1 border border-border">
              <Icon name={item.status === 'pass' ? 'CheckCircleIcon' : 'ClockIcon'} size={14} className={item.status === 'pass' ? 'text-emerald-400 mt-0.5 flex-shrink-0' : 'text-amber-400 mt-0.5 flex-shrink-0'} />
              <div>
                <div className="text-xs font-semibold text-foreground">{item.cap}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{item.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Architecture & Workflows ───────────────────────────────────────────

function ArchitectureTab() {
  return (
    <div className="space-y-6">
      <SectionHeader icon="CpuChipIcon" title="Architecture & Workflows" subtitle="System design, data flows, and orchestration patterns" />

      {/* Architecture layers */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Orchestration Engine Layers</h3>
        <div className="space-y-3">
          {[
            { layer: 'Layer 1 — API Gateway & Auth', desc: 'Next.js API routes with Supabase JWT auth, rate limiting, tenant isolation, and request validation', color: 'blue' },
            { layer: 'Layer 2 — Search Aggregation', desc: 'Parallel supplier queries with timeout management, result normalisation, deduplication, and AI ranking', color: 'violet' },
            { layer: 'Layer 3 — Commerce Rules Engine', desc: 'Dynamic markup, commission, discount, tax, and promotion application with rule priority resolution', color: 'primary' },
            { layer: 'Layer 4 — Package Assembly', desc: 'Multi-component package builder combining flights, hotels, activities, transfers, insurance, and cruises', color: 'emerald' },
            { layer: 'Layer 5 — Booking Orchestration', desc: 'Workflow-driven booking execution with supplier-specific adapters, failover, and payment integration', color: 'amber' },
            { layer: 'Layer 6 — Event Streaming', desc: 'Real-time event bus with webhook dispatch, queue management, and dead-letter handling', color: 'red' },
            { layer: 'Layer 7 — Persistence & Analytics', desc: 'Supabase PostgreSQL with RLS, event log, pricing log, booking stats, and BI analytics', color: 'blue' },
          ].map((item) => (
            <div key={item.layer} className={`flex items-start gap-4 p-4 rounded-lg border ${
              item.color === 'blue' ? 'border-blue-400/20 bg-blue-400/5' :
              item.color === 'violet' ? 'border-violet-400/20 bg-violet-400/5' :
              item.color === 'primary' ? 'border-primary/20 bg-primary/5' :
              item.color === 'emerald' ? 'border-emerald-400/20 bg-emerald-400/5' :
              item.color === 'amber'? 'border-amber-400/20 bg-amber-400/5' : 'border-red-400/20 bg-red-400/5'
            }`}>
              <div className={`text-xs font-bold flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                item.color === 'blue' ? 'bg-blue-400/20 text-blue-400' :
                item.color === 'violet' ? 'bg-violet-400/20 text-violet-400' :
                item.color === 'primary' ? 'bg-primary/20 text-primary' :
                item.color === 'emerald' ? 'bg-emerald-400/20 text-emerald-400' :
                item.color === 'amber'? 'bg-amber-400/20 text-amber-400' : 'bg-red-400/20 text-red-400'
              }`}>{item.layer.split('—')[0].trim().split(' ')[1]}</div>
              <div>
                <div className="text-xs font-semibold text-foreground">{item.layer}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Workflow types */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Orchestration Workflow Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { type: 'Booking Workflow', trigger: 'booking.initiated', steps: 6, desc: 'Availability check → Pricing → Supplier booking → Payment → Confirmation → Event log' },
            { type: 'Cancellation Workflow', trigger: 'booking.cancel_requested', steps: 4, desc: 'Policy validation → Supplier cancel → Refund processing → Customer notification' },
            { type: 'Amendment Workflow', trigger: 'booking.amend_requested', steps: 5, desc: 'Eligibility check → New availability → Apply amendment → Payment adjustment → Notification' },
            { type: 'Refund Workflow', trigger: 'refund.requested', steps: 5, desc: 'Refund eligibility → Supplier refund → Payment refund → Status update → Customer notification' },
            { type: 'Sync Workflow', trigger: 'sync.scheduled', steps: 4, desc: 'Catalogue fetch → Delta comparison → Database upsert → Index update' },
            { type: 'Failover Workflow', trigger: 'supplier.error', steps: 3, desc: 'Error detection → Next supplier selection → Retry with fallback' },
          ].map((wf) => (
            <div key={wf.type} className="rounded-lg border border-border bg-surface-1 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-foreground">{wf.type}</span>
                <span className="text-xs text-muted-foreground">{wf.steps} steps</span>
              </div>
              <div className="text-xs font-mono text-primary mb-2">{wf.trigger}</div>
              <div className="text-xs text-muted-foreground">{wf.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Integration map */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">Integration Map</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { system: 'AI Concierge', desc: 'Gemini-powered supplier selection, itinerary optimisation, and customer recommendations', icon: 'SparklesIcon' },
            { system: 'Booking Engine', desc: 'Unified booking API with multi-supplier adapter pattern and workflow execution', icon: 'CalendarDaysIcon' },
            { system: 'Customer Portal', desc: 'Real-time booking status, itinerary view, amendment and cancellation self-service', icon: 'UserCircleIcon' },
            { system: 'Enterprise Admin', desc: 'Orchestration Center, Commerce Rules, Workflow Designer, Booking Monitor', icon: 'ChartBarIcon' },
            { system: 'Supplier Registry', desc: 'Live supplier status, capability matrix, credential management, health monitoring', icon: 'BuildingStorefrontIcon' },
            { system: 'Analytics', desc: 'Revenue, commission, booking metrics, supplier performance, and conversion analytics', icon: 'PresentationChartLineIcon' },
            { system: 'Payment Orchestrator', desc: 'Multi-gateway payment processing with Razorpay, PayPal, PayU integration', icon: 'CreditCardIcon' },
            { system: 'Webhook Engine', desc: 'Inbound/outbound webhook processing with signature validation and retry logic', icon: 'BoltIcon' },
          ].map((item) => (
            <div key={item.system} className="rounded-lg border border-border bg-surface-1 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary" />
                <span className="text-xs font-semibold text-foreground">{item.system}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Supplier Routing ────────────────────────────────────────────────────

function SupplierRoutingReportTab() {
  return (
    <div className="space-y-6">
      <SectionHeader icon="ArrowsRightLeftIcon" title="Supplier Routing & Failover Strategy" subtitle="Primary routing rules, failover chains, and AI selection logic" />

      {/* Routing matrix */}
      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-surface-1">
          <h3 className="text-sm font-semibold text-foreground">Supplier Routing Matrix</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-1">
              <tr>
                {['Product Type','Primary Supplier','Fallback Chain','Failover Trigger','Threshold','AI Routing'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { type: 'Flights', primary: 'Amadeus', chain: 'Duffel → Sabre → Travelport → AIRIQ', trigger: 'Error / Timeout', threshold: '5s', ai: true },
                { type: 'Hotels', primary: 'RateHawk', chain: 'UP17 → TripJack → TBO', trigger: 'Error / Unavailable', threshold: '5s', ai: true },
                { type: 'Activities', primary: 'Viator', chain: 'Bokun → G Adventures', trigger: 'Error / Price', threshold: '5s', ai: true },
                { type: 'Tours', primary: 'G Adventures', chain: 'Viator → TTC Group → TourRadar', trigger: 'Error / Unavailable', threshold: '5s', ai: true },
                { type: 'Transfers', primary: 'TripJack', chain: 'TBO → UP17', trigger: 'Error / Timeout', threshold: '5s', ai: false },
                { type: 'Insurance', primary: 'TripJack', chain: 'TBO', trigger: 'Error', threshold: '5s', ai: false },
                { type: 'Cruises', primary: 'Crystal Cruises', chain: 'TourRadar', trigger: 'Error / Unavailable', threshold: '10s', ai: true },
              ].map((row) => (
                <tr key={row.type} className="border-t border-border/50 hover:bg-surface-1 transition-colors">
                  <td className="py-3 px-4">
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">{row.type}</span>
                  </td>
                  <td className="py-3 px-4 font-semibold text-foreground">{row.primary}</td>
                  <td className="py-3 px-4 text-muted-foreground font-mono text-xs">{row.chain}</td>
                  <td className="py-3 px-4 text-muted-foreground">{row.trigger}</td>
                  <td className="py-3 px-4 text-muted-foreground">{row.threshold}</td>
                  <td className="py-3 px-4">
                    {row.ai ? (
                      <span className="flex items-center gap-1 text-primary text-xs"><Icon name="SparklesIcon" size={12} /> AI-Powered</span>
                    ) : (
                      <span className="text-zinc-500 text-xs">Rule-based</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Supplier fleet */}
      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-surface-1">
          <h3 className="text-sm font-semibold text-foreground">Full Supplier Fleet — 15 Suppliers</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-1">
              <tr>
                {['Supplier','Category','Status','Health','Uptime','Bookings','Revenue','Commission','Failovers','SLA','AI Routing'].map(h => (
                  <th key={h} className="text-left py-2.5 px-3 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SUPPLIERS.map((s) => (
                <tr key={s.slug} className="border-t border-border/50 hover:bg-surface-1 transition-colors">
                  <td className="py-2.5 px-3 font-semibold text-foreground">{s.name}</td>
                  <td className="py-2.5 px-3 text-muted-foreground">{s.category}</td>
                  <td className="py-2.5 px-3"><StatusBadge status={s.status} /></td>
                  <td className="py-2.5 px-3"><StatusBadge status={s.health} /></td>
                  <td className="py-2.5 px-3 text-muted-foreground">{s.uptime > 0 ? `${s.uptime}%` : 'N/A'}</td>
                  <td className="py-2.5 px-3 text-foreground">{s.bookings.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-foreground">{s.revenue}</td>
                  <td className="py-2.5 px-3 text-emerald-400">{s.commission}</td>
                  <td className="py-2.5 px-3 text-muted-foreground">{s.failovers}</td>
                  <td className="py-2.5 px-3 text-muted-foreground">{s.sla > 0 ? `${s.sla}%` : 'N/A'}</td>
                  <td className="py-2.5 px-3">
                    {s.aiRouting ? <Icon name="SparklesIcon" size={13} className="text-primary" /> : <span className="text-zinc-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Booking Lifecycle ───────────────────────────────────────────────────

function BookingLifecycleTab() {
  const stages = [
    { stage: 'Search', desc: 'Customer initiates search. Orchestration engine queries all relevant suppliers in parallel. Results normalised, ranked by AI, and returned within SLA.', icon: 'MagnifyingGlassIcon', color: 'blue' },
    { stage: 'Select & Price', desc: 'Customer selects product. Commerce rules engine applies markup, commission, discount, tax, and promotion rules. Final price computed.', icon: 'CurrencyDollarIcon', color: 'emerald' },
    { stage: 'Hold / Reserve', desc: 'Availability hold placed with primary supplier. Failover triggered if hold fails. Hold reference stored in orchestration session.', icon: 'ClockIcon', color: 'amber' },
    { stage: 'Confirm Booking', desc: 'Payment processed via payment orchestrator. Supplier booking confirmed. Booking reference generated. Confirmation email dispatched.', icon: 'CheckCircleIcon', color: 'emerald' },
    { stage: 'Post-Booking Sync', desc: 'Booking details synced to Supabase. Event logged to event stream. Webhook dispatched to configured endpoints. Analytics updated.', icon: 'ArrowPathIcon', color: 'primary' },
    { stage: 'Amendment', desc: 'Customer requests change. Eligibility checked against supplier policy. New availability confirmed. Supplier booking amended. Payment adjusted.', icon: 'PencilIcon', color: 'violet' },
    { stage: 'Cancellation', desc: 'Cancellation policy validated. Supplier booking cancelled. Refund calculated per policy. Payment refund initiated. Customer notified.', icon: 'XCircleIcon', color: 'red' },
    { stage: 'Refund Processing', desc: 'Refund eligibility confirmed. Supplier refund initiated. Payment gateway refund processed. Booking status updated to refunded.', icon: 'BanknotesIcon', color: 'amber' },
  ];

  return (
    <div className="space-y-6">
      <SectionHeader icon="ArrowPathIcon" title="Booking Lifecycle" subtitle="End-to-end booking flow from search to refund" />

      <div className="space-y-3">
        {stages.map((s, idx) => (
          <div key={s.stage} className="flex items-start gap-4">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                s.color === 'blue' ? 'bg-blue-400/15 text-blue-400 border border-blue-400/30' :
                s.color === 'emerald' ? 'bg-emerald-400/15 text-emerald-400 border border-emerald-400/30' :
                s.color === 'amber' ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' :
                s.color === 'primary' ? 'bg-primary/15 text-primary border border-primary/30' :
                s.color === 'violet' ? 'bg-violet-400/15 text-violet-400 border border-violet-400/30' :
                'bg-red-400/15 text-red-400 border border-red-400/30'
              }`}>{idx + 1}</div>
              {idx < stages.length - 1 && <div className="w-px h-6 bg-border mt-1" />}
            </div>
            <div className="flex-1 rounded-xl border border-border bg-surface-2 p-4 mb-2">
              <div className="flex items-center gap-2 mb-1">
                <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={14} className={
                  s.color === 'blue' ? 'text-blue-400' : s.color === 'emerald' ? 'text-emerald-400' :
                  s.color === 'amber' ? 'text-amber-400' : s.color === 'primary' ? 'text-primary' :
                  s.color === 'violet' ? 'text-violet-400' : 'text-red-400'
                } />
                <span className="text-xs font-semibold text-foreground">{s.stage}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: Commerce Engine ─────────────────────────────────────────────────────

function CommerceEngineTab() {
  return (
    <div className="space-y-6">
      <SectionHeader icon="CurrencyDollarIcon" title="Commerce Engine" subtitle="Dynamic pricing, markups, commissions, and multi-currency support" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          {
            title: 'Pricing Rule Hierarchy',
            items: [
              'Priority 200+: Tax rules (GST, VAT, TDS)',
              'Priority 100: Standard markup (15% default)',
              'Priority 90: Product-specific markups (flights 10%, hotels 18%)',
              'Priority 80: Supplier commissions (activities 20%)',
              'Priority 70: Corporate discounts (5%)',
              'Priority 60: Membership discounts (Platinum 8%, Gold 5%)',
              'Priority 50: Promotional discounts (time-limited)',
            ],
            icon: 'ListBulletIcon',
          },
          {
            title: 'Multi-Currency Support',
            items: [
              '150+ currencies supported via live FX rates',
              'Base currency: USD with automatic conversion',
              'Per-tenant billing currency configuration',
              'Corporate account billing in negotiated currency',
              'Tax calculation in local jurisdiction currency',
              'Settlement in supplier-preferred currency',
              'FX rate cache with 1-hour expiry',
            ],
            icon: 'CurrencyDollarIcon',
          },
          {
            title: 'Corporate & B2B Features',
            items: [
              'Corporate account creation with company code',
              'Configurable credit limits and credit tracking',
              'Negotiated rate tiers: Standard, Silver, Gold, Platinum',
              'Custom markup profiles per corporate account',
              'Invoice-based billing with NET30/NET60 terms',
              'Multi-traveller booking under single corporate account',
              'Consolidated reporting and spend analytics',
            ],
            icon: 'BuildingOfficeIcon',
          },
          {
            title: 'White-Label Tenant Support',
            items: [
              'Per-tenant branding: logo, colours, domain',
              'Supplier access control per tenant',
              'Tenant-specific markup profiles',
              'Feature flag control per tenant',
              'Isolated data with Supabase RLS',
              'Custom commission structures per tenant',
              'White-label booking confirmation emails',
            ],
            icon: 'SwatchIcon',
          },
        ].map((section) => (
          <div key={section.title} className="rounded-xl border border-border bg-surface-2 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Icon name={section.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">{section.title}</h3>
            </div>
            <ul className="space-y-2">
              {section.items.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Icon name="CheckCircleIcon" size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: AI Decision Logic ───────────────────────────────────────────────────

function AIDecisionLogicTab() {
  return (
    <div className="space-y-6">
      <SectionHeader icon="SparklesIcon" title="AI Decision Logic" subtitle="Gemini-powered orchestration intelligence across all suppliers" />

      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">AI Concierge Orchestration Capabilities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: 'Supplier Selection', desc: 'Gemini evaluates all supplier results against price, availability, SLA history, customer tier, and booking context to select the optimal supplier', icon: 'CpuChipIcon' },
            { title: 'Itinerary Optimisation', desc: 'AI assembles cross-supplier itineraries optimising for price, timing, connections, and customer preferences', icon: 'MapIcon' },
            { title: 'Dynamic Package Assembly', desc: 'AI recommends complementary products (hotel + activities + transfers) based on destination, dates, and customer profile', icon: 'PuzzlePieceIcon' },
            { title: 'Failover Intelligence', desc: 'AI monitors supplier health in real-time and proactively routes away from degraded suppliers before errors occur', icon: 'ArrowPathIcon' },
            { title: 'Price Prediction', desc: 'AI analyses historical pricing trends to recommend optimal booking windows and flag price anomalies', icon: 'ArrowTrendingUpIcon' },
            { title: 'Customer Personalisation', desc: 'AI tailors supplier selection and package recommendations based on customer booking history, preferences, and loyalty tier', icon: 'UserCircleIcon' },
            { title: 'Anomaly Detection', desc: 'AI detects unusual pricing, availability drops, and booking patterns to trigger alerts and automatic responses', icon: 'ExclamationTriangleIcon' },
            { title: 'Natural Language Booking', desc: 'AI Concierge accepts natural language travel requests and orchestrates the full booking workflow autonomously', icon: 'ChatBubbleLeftRightIcon' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3 p-4 rounded-lg bg-surface-1 border border-border">
              <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={15} className="text-primary" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground mb-1">{item.title}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">AI Supplier Selection Decision Tree</h3>
        <div className="space-y-2 text-xs text-muted-foreground">
          {[
            '1. Receive search request with product type, destination, dates, pax, and customer profile',
            '2. Query all eligible suppliers in parallel (based on routing rules)',
            '3. Collect results within timeout window (5s default, 10s for cruises)',
            '4. Normalise prices to base currency (USD)',
            '5. Apply SLA weight: suppliers with >99% uptime get +10% preference score',
            '6. Apply customer tier weight: preferred suppliers for Platinum/Gold members',
            '7. Apply price competitiveness score: lowest price gets highest weight',
            '8. Apply availability score: higher availability = higher preference',
            '9. Compute composite AI score and rank suppliers',
            '10. Select primary supplier; retain ranked fallback list',
            '11. Log AI selection decision with reasoning to event stream',
            '12. Return ranked results to booking engine',
          ].map((step) => (
            <div key={step} className="flex items-start gap-3 py-2 border-b border-border/40">
              <span className="text-primary font-mono flex-shrink-0">{step.split('.')[0]}.</span>
              <span>{step.split('. ')[1]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Production Readiness ────────────────────────────────────────────────

function ProductionReadinessTab() {
  const checks = [
    { category: 'Orchestration Engine', items: [
      { label: 'Multi-supplier search aggregation', status: 'pass' },
      { label: 'AI-powered supplier selection (Gemini)', status: 'pass' },
      { label: 'Dynamic package assembly', status: 'pass' },
      { label: 'Intelligent failover with configurable chains', status: 'pass' },
      { label: 'Unified booking/cancel/amend/refund workflows', status: 'pass' },
      { label: 'Event-driven processing and webhook orchestration', status: 'pass' },
    ]},
    { category: 'Commerce Engine', items: [
      { label: 'Dynamic pricing and markup rules', status: 'pass' },
      { label: 'Commission engine with auto-settlement', status: 'pass' },
      { label: 'Multi-currency support (150+ currencies)', status: 'pass' },
      { label: 'Taxation engine (GST, VAT, TDS)', status: 'pass' },
      { label: 'Corporate and B2B booking support', status: 'pass' },
      { label: 'White-label tenant isolation', status: 'pass' },
    ]},
    { category: 'Supplier Integration', items: [
      { label: 'Viator — Live, catalogue synced', status: 'pass' },
      { label: 'G Adventures — Live, tours synced', status: 'pass' },
      { label: 'Crystal Cruises — Live, voyages synced', status: 'pass' },
      { label: 'Amadeus, Duffel, Sabre, Travelport — Ready', status: 'partial' },
      { label: 'RateHawk, Bokun, TTC Group, TourRadar — Ready', status: 'partial' },
      { label: 'AIRIQ, UP17, TripJack, TBO — Awaiting credentials', status: 'pending' },
    ]},
    { category: 'Infrastructure', items: [
      { label: 'Supabase PostgreSQL with RLS', status: 'pass' },
      { label: 'Next.js 15 API routes with auth middleware', status: 'pass' },
      { label: 'Real-time event streaming', status: 'pass' },
      { label: 'Queue management and workflow engine', status: 'pass' },
      { label: 'Webhook processing and signature validation', status: 'pass' },
      { label: 'Multi-tenant data isolation', status: 'pass' },
    ]},
  ];

  const allPass = checks.flatMap(c => c.items).filter(i => i.status === 'pass').length;
  const allPartial = checks.flatMap(c => c.items).filter(i => i.status === 'partial').length;
  const allPending = checks.flatMap(c => c.items).filter(i => i.status === 'pending').length;
  const total = checks.flatMap(c => c.items).length;

  return (
    <div className="space-y-6">
      <SectionHeader icon="ShieldCheckIcon" title="Production Readiness" subtitle="Comprehensive production readiness assessment" />

      {/* Score */}
      <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/5 p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-3xl font-bold text-emerald-400">{Math.round((allPass / total) * 100)}%</div>
            <div className="text-sm text-foreground font-semibold mt-0.5">Production Readiness Score</div>
            <div className="text-xs text-muted-foreground mt-1">{allPass} passed · {allPartial} partial · {allPending} pending · {total} total checks</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="text-xl font-bold text-emerald-400">{allPass}</div>
              <div className="text-xs text-muted-foreground">Pass</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-amber-400">{allPartial}</div>
              <div className="text-xs text-muted-foreground">Partial</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-zinc-400">{allPending}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 bg-surface-1 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${Math.round((allPass / total) * 100)}%` }} />
        </div>
      </div>

      {/* Checklist */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {checks.map((cat) => (
          <div key={cat.category} className="rounded-xl border border-border bg-surface-2 p-5">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{cat.category}</h3>
            <div className="space-y-2">
              {cat.items.map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <Icon
                    name={item.status === 'pass' ? 'CheckCircleIcon' : item.status === 'partial' ? 'ExclamationCircleIcon' : 'ClockIcon'}
                    size={14}
                    className={item.status === 'pass' ? 'text-emerald-400 flex-shrink-0' : item.status === 'partial' ? 'text-amber-400 flex-shrink-0' : 'text-zinc-500 flex-shrink-0'}
                  />
                  <span className="text-xs text-muted-foreground">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Action items */}
      <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 p-5">
        <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
          <Icon name="ExclamationTriangleIcon" size={16} />
          Recommended Actions to Reach 100% Production Readiness
        </h3>
        <div className="space-y-2">
          {[
            'Add AMADEUS_CLIENT_ID + AMADEUS_CLIENT_SECRET to activate Amadeus flight search',
            'Add DUFFEL_ACCESS_TOKEN to activate Duffel flight booking',
            'Add SABRE_CLIENT_ID + SABRE_CLIENT_SECRET to activate Sabre GDS',
            'Add TRAVELPORT_USERNAME + TRAVELPORT_PASSWORD to activate Travelport',
            'Add AIRIQ_API_KEY + AIRIQ_AGENT_ID to activate AIRIQ domestic flights',
            'Add RATEHAWK_KEY_ID + RATEHAWK_API_KEY to activate RateHawk hotel search',
            'Add BOKUN_ACCESS_KEY + BOKUN_SECRET_KEY to activate Bokun activities',
            'Add UP17_API_KEY to activate UP17 hotels and transfers',
            'Add TRIPJACK_API_KEY to activate TripJack multi-product',
            'Add TBO_API_KEY + TBO_CLIENT_ID to activate TBO multi-product',
          ].map((action, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-amber-300">
              <span className="text-amber-400 font-bold flex-shrink-0">{i + 1}.</span>
              <span>{action}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TravelOrchestrationReportContent() {
  const [activeTab, setActiveTab] = useState('executive');

  const renderTab = () => {
    switch (activeTab) {
      case 'executive': return <ExecutiveSummaryTab />;
      case 'architecture': return <ArchitectureTab />;
      case 'supplier_routing': return <SupplierRoutingReportTab />;
      case 'booking_lifecycle': return <BookingLifecycleTab />;
      case 'commerce': return <CommerceEngineTab />;
      case 'ai_logic': return <AIDecisionLogicTab />;
      case 'production': return <ProductionReadinessTab />;
      default: return <ExecutiveSummaryTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-surface-2 px-6 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
                <Icon name="DocumentChartBarIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Enterprise Travel Orchestration Report</h1>
                <p className="text-xs text-muted-foreground">Architecture · Workflows · Supplier Routing · Booking Lifecycle · Commerce Engine · AI Logic · Production Readiness</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Generated: {new Date().toLocaleDateString()}</span>
              <Link href="/travel-orchestration-center" className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                <Icon name="CpuChipIcon" size={14} />
                Orchestration Center
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-5 overflow-x-auto pb-1">
            {REPORT_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-surface-1'
                }`}
              >
                <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {renderTab()}
      </div>
    </div>
  );
}
